import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { setPageTitle } from './hooks/usePageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api` || '/api';

// Function to extract dominant color from an image
const extractDominantColor = (imgSrc) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Sample at a smaller size for performance
      const sampleSize = 50;
      canvas.width = sampleSize;
      canvas.height = sampleSize;
      
      ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
      
      try {
        const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
        const data = imageData.data;
        
        let r = 0, g = 0, b = 0, count = 0;
        
        // Sample pixels, skipping very dark and very light pixels
        for (let i = 0; i < data.length; i += 4) {
          const red = data[i];
          const green = data[i + 1];
          const blue = data[i + 2];
          
          // Skip very dark pixels (shadows)
          const brightness = (red + green + blue) / 3;
          if (brightness < 30 || brightness > 240) continue;
          
          // Skip very desaturated pixels (grays)
          const max = Math.max(red, green, blue);
          const min = Math.min(red, green, blue);
          const saturation = max === 0 ? 0 : (max - min) / max;
          if (saturation < 0.1) continue;
          
          r += red;
          g += green;
          b += blue;
          count++;
        }
        
        if (count > 0) {
          r = Math.round(r / count);
          g = Math.round(g / count);
          b = Math.round(b / count);
          
          // Ensure the color is vibrant enough for UI use
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          
          // If too desaturated, boost saturation
          if (saturation < 0.3) {
            const boost = 1.5;
            const avg = (r + g + b) / 3;
            r = Math.min(255, Math.round(avg + (r - avg) * boost));
            g = Math.min(255, Math.round(avg + (g - avg) * boost));
            b = Math.min(255, Math.round(avg + (b - avg) * boost));
          }
          
          resolve({ r, g, b });
        } else {
          // Fallback to a nice blue if extraction fails
          resolve({ r: 59, g: 130, b: 246 });
        }
      } catch (e) {
        // CORS or other error - fallback color
        resolve({ r: 59, g: 130, b: 246 });
      }
    };
    
    img.onerror = () => {
      // Fallback color on error
      resolve({ r: 59, g: 130, b: 246 });
    };
    
    img.src = imgSrc;
  });
};

// Convert RGB to HSL for generating lighter/darker variants
const rgbToHsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
};

function PropertyWebsite() {
  const { slug } = useParams();
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [accentColor, setAccentColor] = useState({ r: 59, g: 130, b: 246 }); // Default blue

  const getImageUrl = useCallback((image, forDownload = false) => {
    return `${API}/property-website/${slug}/media/${image.id}${forDownload ? '?download=true' : ''}`;
  }, [slug]);

  // Dynamic page title: show the property address so the browser tab and any
  // social-share preview reflect the specific listing rather than the generic
  // site title.
  useEffect(() => {
    const address = propertyData?.property?.property_address || propertyData?.property?.address;
    if (address) {
      const shortAddr = address.split(',')[0].trim() || address;
      setPageTitle(`${shortAddr} — Property Photos`);
    }
    return () => setPageTitle();
  }, [propertyData?.property?.property_address, propertyData?.property?.address]);

  useEffect(() => {
    let cancelled = false;
    const fetchPropertyData = async () => {
      try {
        const response = await axios.get(`${API}/property-website/${slug}`);
        if (cancelled) return;
        setPropertyData(response.data);
      } catch (err) {
        console.error('Error fetching property:', err);
        if (!cancelled) setError('Property not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPropertyData();
    return () => { cancelled = true; };
  }, [slug]);

  // Color extraction runs in the background once we have property data — it
  // must NEVER block the loading spinner from clearing (a hanging image load
  // would otherwise leave the entire page stuck on the spinner).
  useEffect(() => {
    if (!propertyData?.images?.length) return;
    let cancelled = false;
    const firstImage = propertyData.images[0];
    const firstImageUrl = `${API}/property-website/${slug}/media/${firstImage.id}`;
    // Safety timeout — never wait more than 4 seconds for a color extraction.
    const timeout = window.setTimeout(() => { if (!cancelled) cancelled = true; }, 4000);
    extractDominantColor(firstImageUrl)
      .then((color) => { if (!cancelled) setAccentColor(color); })
      .catch(() => { /* keep default accent */ })
      .finally(() => window.clearTimeout(timeout));
    return () => { cancelled = true; window.clearTimeout(timeout); };
  }, [propertyData?.images, slug]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Generate color variants
  const rgb = `rgb(${accentColor.r}, ${accentColor.g}, ${accentColor.b})`;
  const rgbHover = `rgb(${Math.max(0, accentColor.r - 20)}, ${Math.max(0, accentColor.g - 20)}, ${Math.max(0, accentColor.b - 20)})`;
  const rgbLight = `rgba(${accentColor.r}, ${accentColor.g}, ${accentColor.b}, 0.1)`;
  const rgbMedium = `rgba(${accentColor.r}, ${accentColor.g}, ${accentColor.b}, 0.2)`;
  
  // Determine if accent color is light or dark for text contrast
  const brightness = (accentColor.r * 299 + accentColor.g * 587 + accentColor.b * 114) / 1000;
  const textOnAccent = brightness > 150 ? '#1a1a1a' : '#ffffff';

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: rgb }}></div>
      </div>
    );
  }

  if (error || !propertyData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Property Not Found</h1>
          <p className="text-gray-600">The property you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const { property, images = [], client, floor_plans = [], tours_3d = [] } = propertyData;
  const heroImage = images[0];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Broker/Company Logo */}
            {client?.avatar ? (
              <img src={client.avatar} alt={client.company || 'Company'} className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: rgb }}>
                <svg className="w-6 h-6" style={{ color: textOnAccent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
            )}
            <span className="font-semibold text-gray-900">{client?.company || 'Property Showcase'}</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('overview')} className="text-gray-600 text-sm font-medium transition-colors hover:opacity-70" style={{ '--hover-color': rgb }} onMouseEnter={(e) => e.target.style.color = rgb} onMouseLeave={(e) => e.target.style.color = ''}>Overview</button>
            <button onClick={() => scrollToSection('details')} className="text-gray-600 text-sm font-medium transition-colors" onMouseEnter={(e) => e.target.style.color = rgb} onMouseLeave={(e) => e.target.style.color = ''}>Details</button>
            {images.length > 0 && <button onClick={() => scrollToSection('gallery')} className="text-gray-600 text-sm font-medium transition-colors" onMouseEnter={(e) => e.target.style.color = rgb} onMouseLeave={(e) => e.target.style.color = ''}>Gallery</button>}
            {floor_plans.length > 0 && <button onClick={() => scrollToSection('floorplans')} className="text-gray-600 text-sm font-medium transition-colors" onMouseEnter={(e) => e.target.style.color = rgb} onMouseLeave={(e) => e.target.style.color = ''}>Floor Plans</button>}
            {tours_3d.length > 0 && <button onClick={() => scrollToSection('tours3d')} className="text-gray-600 text-sm font-medium transition-colors" onMouseEnter={(e) => e.target.style.color = rgb} onMouseLeave={(e) => e.target.style.color = ''}>3D Tours</button>}
            <button onClick={() => scrollToSection('location')} className="text-gray-600 text-sm font-medium transition-colors" onMouseEnter={(e) => e.target.style.color = rgb} onMouseLeave={(e) => e.target.style.color = ''}>Location</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-600 text-sm font-medium transition-colors" onMouseEnter={(e) => e.target.style.color = rgb} onMouseLeave={(e) => e.target.style.color = ''}>Contact</button>
          </div>
          <button 
            onClick={() => scrollToSection('contact')} 
            className="px-5 py-2 rounded-full text-sm font-medium transition-all"
            style={{ backgroundColor: rgb, color: textOnAccent }}
            onMouseEnter={(e) => e.target.style.backgroundColor = rgbHover}
            onMouseLeave={(e) => e.target.style.backgroundColor = rgb}
          >
            Schedule Tour
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <div id="overview" className="pt-20">
        <div className="relative h-[75vh] overflow-hidden">
          {heroImage ? (
            <img
              src={getImageUrl(heroImage)}
              alt={property.address}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${rgbLight} 0%, ${rgbMedium} 100%)` }}>
              <svg className="w-32 h-32" style={{ color: rgbMedium }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        </div>

        {/* Property Info Card - Overlapping Hero */}
        <div className="max-w-6xl mx-auto px-6 -mt-32 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {property.address}
                </h1>
                {property.price && !isNaN(Number(property.price)) && (
                  <p className="text-3xl md:text-4xl font-bold" style={{ color: rgb }}>
                    ${Number(property.price).toLocaleString()}
                  </p>
                )}
              </div>
              
              {/* Quick Stats */}
              <div className="flex gap-8">
                {property.bedrooms && (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900">{property.bedrooms}</p>
                    <p className="text-gray-500 text-sm">Beds</p>
                  </div>
                )}
                {property.bathrooms && (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900">{property.bathrooms}</p>
                    <p className="text-gray-500 text-sm">Baths</p>
                  </div>
                )}
                {property.sqft && (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900">{Number(property.sqft).toLocaleString()}</p>
                    <p className="text-gray-500 text-sm">Sq Ft</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div id="details" className="max-w-6xl mx-auto px-6 py-20 scroll-mt-20">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Description */}
          {property.description && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">About This Property</h2>
              <p className="text-gray-600 leading-relaxed text-lg whitespace-pre-wrap">
                {property.description}
              </p>
            </div>
          )}

          {/* Property Details Grid */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Property Details</h2>
            <div className="grid grid-cols-2 gap-6">
              {property.bedrooms && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Bedrooms</p>
                  <p className="text-xl font-semibold text-gray-900">{property.bedrooms}</p>
                </div>
              )}
              {property.bathrooms && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Bathrooms</p>
                  <p className="text-xl font-semibold text-gray-900">{property.bathrooms}</p>
                </div>
              )}
              {property.sqft && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Square Feet</p>
                  <p className="text-xl font-semibold text-gray-900">{Number(property.sqft).toLocaleString()}</p>
                </div>
              )}
              {property.year_built && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Year Built</p>
                  <p className="text-xl font-semibold text-gray-900">{property.year_built}</p>
                </div>
              )}
              {property.lot_size && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Lot Size</p>
                  <p className="text-xl font-semibold text-gray-900">{property.lot_size}</p>
                </div>
              )}
              {property.parking && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Garage</p>
                  <p className="text-xl font-semibold text-gray-900">{property.parking}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features */}
        {property.features && property.features.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Features & Amenities</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {property.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 rounded-lg px-4 py-3" style={{ backgroundColor: rgbLight }}>
                  <svg className="w-5 h-5 flex-shrink-0" style={{ color: rgb }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Photo Gallery */}
      {images.length > 0 && (
        <div id="gallery" className="py-20 scroll-mt-20" style={{ backgroundColor: rgbLight }}>
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Photo Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((img, idx) => (
                <div 
                  key={img.id} 
                  className={`cursor-pointer overflow-hidden rounded-xl hover:opacity-90 transition-opacity ${idx === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                  onClick={() => setSelectedImage(img)}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`Property photo ${idx + 1}`}
                    className="w-full h-full object-cover aspect-[4/3]"
                    style={idx === 0 ? { aspectRatio: '4/3' } : {}}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Floor Plans Section */}
      {floor_plans.length > 0 && (
        <div id="floorplans" className="py-20 scroll-mt-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Floor Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {floor_plans.map((plan, idx) => (
                <div 
                  key={plan.id || idx} 
                  className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => plan.type === 'image' && window.open(plan.url.startsWith('http') ? plan.url : `${API}/property-website/${slug}/floor-plan/${plan.id}`, '_blank')}
                >
                  {plan.type === 'image' ? (
                    <img
                      src={plan.url.startsWith('http') ? plan.url : `${API}/property-website/${slug}/floor-plan/${plan.id}`}
                      alt={plan.name || `Floor Plan ${idx + 1}`}
                      className="w-full h-auto object-contain bg-white"
                      style={{ maxHeight: '500px' }}
                    />
                  ) : (
                    <div className="w-full h-64 flex items-center justify-center bg-gray-100">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                    </div>
                  )}
                  <div className="p-4 border-t border-gray-200">
                    <p className="font-medium text-gray-900">{plan.name || `Floor Plan ${idx + 1}`}</p>
                    <p className="text-sm text-gray-500 mt-1">Click to view full size</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 3D Tours Section */}
      {tours_3d.length > 0 && (
        <div id="tours3d" className="py-20 scroll-mt-20" style={{ backgroundColor: rgbLight }}>
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">3D Virtual Tours</h2>
            <div className="space-y-6">
              {tours_3d.map((tour, idx) => (
                <div key={tour.id || idx} className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                  {/* Embed iframe for Matterport/iGuide if possible */}
                  {(tour.url.includes('matterport.com') || tour.url.includes('my.matterport.com')) ? (
                    <iframe
                      src={tour.url.includes('?') ? tour.url : `${tour.url}?play=1`}
                      width="100%"
                      height="500"
                      frameBorder="0"
                      allowFullScreen
                      title={tour.name || `3D Tour ${idx + 1}`}
                      className="w-full"
                    />
                  ) : (
                    <div className="relative">
                      <div 
                        className="w-full h-80 flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${rgb}20, ${rgb}40)` }}
                      >
                        <div className="text-center">
                          <div 
                            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                            style={{ backgroundColor: rgb }}
                          >
                            <svg className="w-10 h-10" style={{ color: textOnAccent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                          </div>
                          <a
                            href={tour.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all"
                            style={{ backgroundColor: rgb, color: textOnAccent }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = rgbHover}
                            onMouseLeave={(e) => e.target.style.backgroundColor = rgb}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Launch 3D Tour
                          </a>
                        </div>
                      </div>
                      <div className="p-4 border-t border-gray-200">
                        <p className="font-medium text-gray-900">{tour.name || `3D Tour ${idx + 1}`}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Map Section */}
      <div id="location" className="py-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Location</h2>
          <div className="rounded-2xl overflow-hidden shadow-lg" style={{ height: '400px' }}>
            <iframe
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0 }}
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(property.address)}&zoom=15`}
              allowFullScreen
              title="Property Location"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="py-20 scroll-mt-20" style={{ backgroundColor: rgb }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ color: textOnAccent }}>Ready to see this property?</h2>
          <p className="text-lg mb-8" style={{ color: textOnAccent, opacity: 0.8 }}>Schedule a private tour today and experience it for yourself.</p>
          {client?.name && (
            <div className="rounded-2xl p-8 inline-block" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
              {/* Agent Avatar */}
              <div className="flex flex-col items-center gap-4">
                {client.avatar ? (
                  <img src={client.avatar} alt={client.name} className="w-24 h-24 rounded-full object-cover border-4" style={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                ) : (
                  <div className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: textOnAccent }}>
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="text-sm mb-1" style={{ color: textOnAccent, opacity: 0.7 }}>Listed by</p>
                  <p className="text-xl font-semibold" style={{ color: textOnAccent }}>{client.name}</p>
                  {client.company && <p className="text-sm" style={{ color: textOnAccent, opacity: 0.8 }}>{client.company}</p>}
                </div>
                <div className="flex flex-col gap-2 mt-2">
                  {client.email && (
                    <a href={`mailto:${client.email}`} className="flex items-center gap-2 text-sm transition-colors" style={{ color: textOnAccent, opacity: 0.9 }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {client.email}
                    </a>
                  )}
                  {client.phone && (
                    <a href={`tel:${client.phone}`} className="flex items-center gap-2 text-sm transition-colors" style={{ color: textOnAccent, opacity: 0.9 }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {client.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white hover:text-gray-300"
            onClick={() => setSelectedImage(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={getImageUrl(selectedImage, true)}
            alt="Property"
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default PropertyWebsite;
